<template>
  <CDropdown head-class="cursor-pointer">
    <template #head>
      <div class="flex-y-center gap-2">
        <CAvatar :image="user?.avatar_url" :size="!mobile ? 'md' : 'sm'" />
        <div
          v-if="!mobile"
          class="flex flex-col text-sm font-semibold text-left leading-20 !break-all"
        >
          <span>{{ user?.full_name?.split(" ")?.[0] }}</span>
          <span>{{ user?.full_name?.split(" ")?.[1] }}</span>
        </div>
      </div>
    </template>
    <div class="p-3">
      <CProfile
        :email="user?.email"
        :name="user?.full_name"
        :avatar="user?.avatar_url"
      />
      <div class="flex border-b border-[#F5F6F7] pb-4 space-x-1">
        <Badge variant="info" class="w-auto shrink-0 mt-px grow justify-center">
          ID: {{ user?.display_id }}
        </Badge>
        <Badge
          class="w-auto shrink-0 mt-px capitalize flex items-center grow justify-center"
          :variant="user?.is_email_verified ? 'verified' : 'warning'"
        >
          <span class="icon-verified text-lg !leading-20 mr-1" />{{
            user?.is_email_verified ? $t("verified") : $t("unverified")
          }}
        </Badge>
      </div>
      <ul>
        <li
          class="transition-200 flex flex-col gap-1 text-base font-semibold w-full text-gray-450 hover:bg-gray-250 rounded-xl"
          v-for="(item, idx) in dropdownItems"
          :key="idx"
          :class="item.styles"
          @click="item.action"
        >
          <div
            class="flex-y-center px-3 py-2 border-b border-[#F5F6F7] gap-2 capitalize"
            :class="{ '!border-b-0': idx === dropdownItems.length - 1 }"
          >
            <i :class="item?.icon" class="text-lg font-semibold" />
            {{ item.label }}
          </div>
        </li>
      </ul>
    </div>
  </CDropdown>
</template>

<script lang="ts" setup>
import CDropdown from "@/components/Common/Dropdown/CDropdown.vue";
import { computed } from "vue";
import CAvatar from "@/components/CAvatar.vue";
import { useI18n } from "vue-i18n";
import { useRoute } from "vue-router";
import { useAuthStore } from "@/modules/Auth/stores";
import CProfile from "@/layout/Dashboard/components/CProfile.vue";
import { Badge } from "@/components/ui/badge";
import { useLocalizedNavigation } from "@/composables/useLocalizedNavigation";

interface Props {
  user?: {
    fullName: string;
    avatar: string;
    subtitle: string;
  };
  mobile?: boolean;
}
withDefaults(defineProps<Props>(), {
  mobile: false,
});

const route = useRoute();
const { t } = useI18n();
const store = useAuthStore();
const { navigateTo } = useLocalizedNavigation();

const user = computed(() => store.user);
interface IDropdownItem {
  label: string;
  styles?: string;
  action: () => void;
  icon: string;
}

const dropdownItems = computed<IDropdownItem[]>(() => [
  {
    label: t("profile.title"),
    action: () =>
      navigateTo({
        path: "/settings",
        query: {
          profile: "profile",
        },
      }),
    styles: "hover:!text-dark",
    icon: "icon-user",
  },
  {
    label: t("directory"),
    action: () =>
      navigateTo({
        path: "/guide",
      }),
    styles: "hover:!text-dark",
    icon: "icon-menu-docs",
  },
  {
    label: t("saraf_support"),
    action: () => window.open("https://t.me/sarafrobot_support", "_blank"),
    styles: "hover:!text-dark",
    icon: "icon-message-question-stroke",
  },
  {
    label: t("log_out"),
    styles: "text-red-500 hover:!bg-red-50",
    action: () => logout(),
    icon: "icon-log-out",
  },
]);
function logout() {
  store?.logout();
  if (route.meta.layout === "landing") {
    navigateTo({ name: "Landing" });
    window.location.reload();
  } else {
    navigateTo({ name: "PAuthLogin" });
  }
}
</script>
